/* ============================================================
   VITALCASA CRM — ModalNuevaPrecaptacion
   Modal para crear una precaptación con:
   - Datos del propietario (nombre, tel, email, operación, procedencia)
   - Agente (autorellenado + selector)
   - Toggle quién lo trabaja: Agente / Ana
   - Fotos identificativas → Ana extrae datos automáticamente
   ============================================================ */

import { useState, useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from './ThemeProvider';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import AddressAutocompleteInput from './AddressAutocompleteInput';
import { resolverPoblacionPlaces } from '../utils/resolverLocalidadDesdePlaces';
import { parseTipoViaCalle } from '../utils/parseTipoViaCalle';
import { emojiVisualProcedencia } from '../utils/procedenciaColor';
import {
  datosExtrasCaptacion,
  esCaptacionDesdeScraping,
  poblacionPipelineCard,
  provinciaPipelineCard,
  codigoPostalPipelineCard,
  pareceCodigoInePoblacion,
} from '../utils/captacionDisplay';
import {
  esTituloDePortalScraping,
  tituloCrmUsable,
  tipoCrmUsable,
  estadoCrmUsable,
  calefaccionCrmUsable,
  cocinaCrmUsable,
  piscinaCrmUsable,
  orientacionCrmUsable,
} from '../utils/tituloPortalScraping';
import {
  CAPTACION_IDIOMA_OPTIONS,
  normalizeLanguageCode,
  resolverIdiomaPropietarioCaptacion,
  patchIdiomaEnExtrasCaptacion,
} from '../utils/captacionIdioma';
import PanelHistorialCaptacion from './PanelHistorialCaptacion';
import usePermiso from '../hooks/usePermiso';
import { useCaptacionRelacionContacto } from '../hooks/useCaptacionRelacionContacto';
import ModalRelacionContactoExistente from './ModalRelacionContactoExistente';
import { formatPrecioVentaEntero, parsePrecioVentaEntero } from '../utils/moneyParse';

/** Azul del chip «Todos» del historial (`PanelHistorialCaptacion`). */
const PRECAP_CAMPO_AZUL = '#3b82f6';

function campoTieneValor(v) {
  return v != null && String(v).trim() !== '';
}

function formatearPrecioCampo(raw) {
  if (raw == null || raw === '') return '';
  const n = parsePrecioVentaEntero(raw);
  return Number.isFinite(n) ? formatPrecioVentaEntero(n) : '';
}

/** URLs de la columna fotos (el listado del Pipeline solo manda la portada). */
function urlsFotosCaptacion(cap) {
  const raw = cap?.fotos;
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map((u) => {
    if (typeof u === 'string') return u.trim();
    if (u && typeof u === 'object') {
      return String(u.url_thumb || u.url || u.url_display || u.src || '').trim();
    }
    return '';
  }).filter(Boolean);
}

function fotosUiDesdeUrls(urls) {
  return urls.map((url) => ({ file: null, preview: url, esExistente: true }));
}

/** El listado deja 1 URL; si llega la ficha completa, ampliar la tira sin pisar fotos nuevas. */
function ampliarFotosSiFaltan(prev, urls) {
  if (!Array.isArray(prev) || prev.some((f) => f && f.file)) return prev;
  const full = Array.isArray(urls) ? urls.filter(Boolean) : [];
  if (full.length <= prev.length) return prev;
  return fotosUiDesdeUrls(full);
}
// ── Mapa de etapas para badge ────────────────────────────────
const ETAPAS_MAPA = {
  precaptacion:   { label: 'Precaptación',      color: '#eab308' },
  contactado_cap: { label: 'Contactados',        color: '#f97316' },
  visita_cap:     { label: 'Visita Concertada',  color: '#ef4444' },
  revisar_cap:    { label: 'Para Revisar',       color: '#ec4899' },
  captacion:      { label: 'Captación',          color: '#3b82f6' },
};

/** Chips fijos en una sola línea; el resto en «+ Procedencias». */
const PROCEDENCIAS_FIJAS_NOMBRES = ['Boca a boca', 'Cartel', 'Escaparate'];
const PORTALES_MARKETPLACE_SLUGS = ['idealista', 'fotocasa', 'pisos.com', 'pisos', 'habitaclia'];

function slugProcedencia(s) {
  return String(s || '').trim().toLowerCase();
}

function esPortalMarketplaceNombre(nombre) {
  const s = slugProcedencia(nombre);
  if (!s) return false;
  return PORTALES_MARKETPLACE_SLUGS.some((p) => s === p || s.includes(p));
}

/** «Scraping Idealista (Pipeline agente)» → se muestra vía fuenteVisibleEnLead como MarketPlace… */
function normalizarFuenteProcedencia(fuente, portal) {
  const raw = String(fuente || '').trim();
  if (!raw) {
    const p = String(portal || '').trim().toLowerCase();
    const map = { idealista: 'Idealista', fotocasa: 'Fotocasa', pisos: 'Pisos.com', habitaclia: 'Habitaclia' };
    return map[p] || '';
  }
  // Formulario: chip de portal (Idealista), no el prefijo MarketPlace/Scraping.
  const m = raw.match(/^(?:Scraping|MarketPlace)\s+(.+?)(?:\s*\(|$)/i);
  if (m) return m[1].trim();
  return raw;
}

/** Si calle trae el número y numero está vacío, separa (captaciones antiguas de scraping). */
function separarCalleNumero(calle, numero) {
  if (numero) return { calle: calle || '', numero: String(numero) };
  const raw = String(calle || '').trim();
  if (!raw) return { calle: '', numero: '' };
  const m = raw.match(/^(.*?)[,\s]+(?:n[ºo°.]?\s*)?(\d{1,5}[a-zA-Z]?)\s*$/i)
    || raw.match(/^(.*?)\s+(\d{1,4}[a-zA-Z]?)\s*$/i);
  if (m && !/^\d{5}$/.test(m[2])) {
    return { calle: m[1].trim(), numero: m[2].trim() };
  }
  return { calle: raw, numero: '' };
}

const OPERACIONES = [
  { id: 'Venta',    emoji: '🏷️', label: 'Venta' },
  { id: 'Alquiler', emoji: '🔑', label: 'Alquiler' },
  { id: 'Traspaso', emoji: '🔄', label: 'Traspaso' },
];

const ml = {
  green:       '#059669',
  greenFrom:   '#065f46',
  greenTo:     '#059669',
  greenLight:  '#10b981',
  greenText:   '#d1fae5',
  greenShadow: 'rgba(5,150,105,0.35)',
  greenBg:     'rgba(5,150,105,0.08)',
  greenBorder: 'rgba(5,150,105,0.25)',
  chipText:    '#34d399',
};

export default function ModalNuevaPrecaptacion({ isOpen, onClose, onCreated, onRemoteUpdate, captacion, camposFaltantes = [] }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === 'dark';
  const { usuario } = useAuth();
  const { puede } = usePermiso();
  const verDatos = puede('pl_capt_ficha_datos', 'ver');
  const modDatos = puede('pl_capt_ficha_datos', 'modificar');
  const verOperacionFicha = puede('pl_capt_ficha_operacion', 'ver');
  const modOperacionFicha = puede('pl_capt_ficha_operacion', 'modificar');
  const verFichaFrio = puede('pl_capt_ficha_frio', 'ver');
  const verFichaEliminar = puede('pl_capt_ficha_eliminar', 'ver');
  const tituloSinPermiso = 'No tienes permiso para modificar este campo';

  const relacionContacto = useCaptacionRelacionContacto({
    excludeCaptacionId: captacion?.id || null,
  });

  const [agentes, setAgentes] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [tiposVia, setTiposVia] = useState([]);
  const [procedencias, setProcedencias] = useState([]);
  const [procDropdownOpen, setProcDropdownOpen] = useState(false);
  const procDropdownRef = useRef(null);
  const procBtnRef = useRef(null);
  const [procMenuPos, setProcMenuPos] = useState(null);
  const [form, setForm] = useState({
    nombre: '', apellido: '', telefono: '', email: '',
    fuente: '', operacion: '',
    agente_id: '',
    quien_trabaja: 'agente', // 'agente' | 'ana'
    notas: '',
    // Campos que Ana puede rellenar desde fotos:
    calle: '', numero: '', bloque: '', escalera: '', piso: '', puerta: '',
    poblacion: '', provincia: '', codigo_postal: '', tipo_inmueble: '',
    precio: '',
    tipo_via_id: '', tipo_via: '',
    url_anuncio: '',
    idioma: 'es',
  });
  // Campos extra para datos_extras (campos de captación para Para Revisar)
  const [formExtras, setFormExtras] = useState({
    titulo: '', precio_propietario: '', honorarios_porcentaje: '',
    resumen: '', firma_captacion: false,
  });
  const [fotos, setFotos] = useState([]);
  const [accionArchivo, setAccionArchivo] = useState(null); // 'frio' | 'eliminado'
  const [motivoArchivo, setMotivoArchivo] = useState('');
  const [archivando, setArchivando] = useState(false); // Array de { file, preview, analizando, resultado }
  const [guardando, setGuardando] = useState(false);
  const formDirtyRef = useRef(false);
  const [analizando, setAnalizando] = useState(false);
  const [error, setError] = useState('');
  const [creado, setCreado] = useState(false);
  const [autoAvanzado, setAutoAvanzado] = useState(false);
  const [fotoDragIdx, setFotoDragIdx] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const inputFotoRef = useRef(null);

  // Visor a pantalla completa (lupa): Esc cierra, flechas navegan.
  useEffect(() => {
    if (lightboxIdx == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setLightboxIdx(null);
        return;
      }
      if (!fotos.length) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setLightboxIdx((i) => (i > 0 ? i - 1 : fotos.length - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setLightboxIdx((i) => (i < fotos.length - 1 ? i + 1 : 0));
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightboxIdx, fotos.length]);

  // ── Drag del modal ───────────────────────────────────────────
  const [pos, setPos] = useState(null);
  const dragRef = useRef(null);
  const dragOffsetRef = useRef(null);
  const startDrag = (e) => {
    if (e.target.closest('button')) return;
    const rect = dragRef.current?.getBoundingClientRect();
    if (!rect) return;
    const ox = e.clientX - rect.left;
    const oy = e.clientY - rect.top;
    dragOffsetRef.current = { ox, oy, w: rect.width, h: rect.height };
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      if (!dragOffsetRef.current) return;
      const { ox, oy, w, h } = dragOffsetRef.current;
      setPos({ x: Math.max(0, Math.min(window.innerWidth - w, ev.clientX - ox)), y: Math.max(0, Math.min(window.innerHeight - h, ev.clientY - oy)) });
    };
    const onUp = () => { dragOffsetRef.current = null; document.body.style.userSelect = ''; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Determina si un campo_key está en la lista de faltantes
  const esFaltante = (key) => camposFaltantes.some(f => f.campo_key === key);

  const lastSyncedUpdatedAtRef = useRef(null);

  const hidratarDesdeCaptacion = (cap, { force = false } = {}) => {
    if (!cap) return;
    // No pisar lo que el agente está editando (p. ej. al añadir fotos + poll remoto).
    if (formDirtyRef.current && !force) return;
    const de = datosExtrasCaptacion(cap);
    const dir = separarCalleNumero(cap.calle || '', cap.numero || de.numero || '');
    setForm({
      nombre:        cap.nombre        || '',
      apellido:      cap.apellido      || '',
      telefono:      cap.telefono      || '',
      email:         cap.email         || '',
      fuente:        normalizarFuenteProcedencia(cap.fuente, de.portal),
      operacion:     cap.operacion     || '',
      // String() evita que el <select> muestre «Sin asignar» con id UUID/número que no casan.
      agente_id:     (cap.agente_id != null && String(cap.agente_id).trim() !== '')
        ? String(cap.agente_id)
        : (de.pipeline_owner_agente_id != null && String(de.pipeline_owner_agente_id).trim() !== ''
          ? String(de.pipeline_owner_agente_id)
          : ''),
      quien_trabaja: cap.quien_trabaja || 'agente',
      notas:         cap.notas         || '',
      calle:         dir.calle,
      numero:        dir.numero,
      bloque:        de.bloque    || '',
      escalera:      de.escalera  || '',
      piso:          de.piso || de.planta || '',
      puerta:        de.puerta    || '',
      // PWA/móvil a menudo guarda el id INE (03063) en captaciones.poblacion;
      // el CP real va en extras. Misma regla que la tarjeta del Pipeline.
      poblacion:     poblacionPipelineCard(cap, de)
        || (!pareceCodigoInePoblacion(cap.poblacion) ? (cap.poblacion || '') : '')
        || (!pareceCodigoInePoblacion(de.municipio) ? (de.municipio || '') : '')
        || '',
      provincia:     provinciaPipelineCard(cap, de) || '',
      codigo_postal: codigoPostalPipelineCard(cap, de) || '',
      tipo_inmueble: cap.tipo_inmueble || '',
      precio: formatearPrecioCampo(cap.precio_venta ?? de.precio_venta ?? de.precio_propietario ?? ''),
      tipo_via_id:   de.tipo_via_id || '',
      tipo_via:      de.tipo_via || '',
      url_anuncio:   de.url_original || '',
      idioma:        resolverIdiomaPropietarioCaptacion(cap),
    });
    setFormExtras({
      // Título CRM ≠ título de portal (Idealista/Fotocasa/Habitaclia/Pisos…). Portal → titulo_portal.
      titulo: tituloCrmUsable(de.web_titulo || de.titulo, de),
      titulo_portal: de.titulo_portal
        || (esTituloDePortalScraping(de.titulo, de) ? String(de.titulo).trim() : '')
        || '',
      precio_propietario:    formatearPrecioCampo(de.precio_propietario || cap.precio_venta || de.precio_venta || ''),
      honorarios_porcentaje: de.honorarios_porcentaje || '',
      resumen:               de.resumen               || '',
      firma_captacion:       de.firma_captacion === true || de.firma_captacion === 'true',
    });
    if (cap.fotos && cap.fotos.length > 0) {
      setFotos(cap.fotos.map(url => ({ file: null, preview: url, esExistente: true })));
    } else {
      setFotos([]);
    }
    lastSyncedUpdatedAtRef.current = cap.updated_at || null;
    formDirtyRef.current = false;
  };

  useEffect(() => {
    if (!isOpen) {
      formDirtyRef.current = false;
      lastSyncedUpdatedAtRef.current = null;
      return;
    }
    cargarAgentes();
    cargarTipos();
    cargarProcedencias();
    setProcDropdownOpen(false);
    if (captacion) {
      if (captacion.updated_at && captacion.updated_at === lastSyncedUpdatedAtRef.current) {
        // Misma ficha, pero el listado solo traía la portada: al llegar el GET completo, mostrar el resto.
        setFotos((prev) => ampliarFotosSiFaltan(prev, urlsFotosCaptacion(captacion)));
      } else {
        // Sin force: si el agente está editando, no pisar el formulario.
        hidratarDesdeCaptacion(captacion);
      }
    } else {
      lastSyncedUpdatedAtRef.current = null;
      formDirtyRef.current = false;
      if (usuario?.agente_id) {
        setForm(prev => ({ ...prev, agente_id: String(usuario.agente_id) }));
      }
      setFormExtras({ titulo: '', precio_propietario: '', honorarios_porcentaje: '', resumen: '', firma_captacion: false });
    }
    setAutoAvanzado(false);
  }, [isOpen, captacion?.id, captacion?.updated_at, Array.isArray(captacion?.fotos) ? captacion.fotos.length : 0, usuario]);

  // Aviso si la ficha ya trae teléfono/email (p. ej. Scraping / Ana)
  useEffect(() => {
    if (!isOpen) {
      relacionContacto.resetSeen();
      relacionContacto.closeQuiet();
      return;
    }
    const tel = captacion?.telefono || form.telefono;
    const em = captacion?.email || form.email;
    if (tel || em) {
      void relacionContacto.checkNow(tel, em, { force: true });
    }
    // Solo al abrir / cambiar de captación
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, captacion?.id]);

  // Espejo en vivo: si la APP (u otro cliente) guarda, refrescar el modal abierto
  useEffect(() => {
    if (!isOpen || !captacion?.id || guardando) return undefined;
    let cancelled = false;
    const sync = async () => {
      try {
        const res = await api.get(`/captaciones/${captacion.id}`);
        const data = res.data?.data || res.data;
        if (cancelled || !data?.id) return;
        setFotos((prev) => ampliarFotosSiFaltan(prev, urlsFotosCaptacion(data)));
        const ts = data.updated_at || '';
        if (!ts || ts === lastSyncedUpdatedAtRef.current) return;
        hidratarDesdeCaptacion(data);
        if (typeof onRemoteUpdate === 'function') onRemoteUpdate(data);
      } catch (e) { /* noop */ }
    };
    const t = setInterval(sync, 4000);
    sync();
    return () => { cancelled = true; clearInterval(t); };
  }, [isOpen, captacion?.id, guardando, onRemoteUpdate]);

  // MarketPlace → Pipeline: completar enlace / huecos desde el anuncio de scraping si faltan
  useEffect(() => {
    if (!isOpen || !captacion?.id) return undefined;
    if (!esCaptacionDesdeScraping(captacion)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/scraping/por-captacion/${captacion.id}`);
        const anuncio = res.data;
        if (cancelled || !anuncio) return;
        const car = anuncio.caracteristicas && typeof anuncio.caracteristicas === 'object'
          ? anuncio.caracteristicas : {};
        const contactoNom = String(anuncio.nombre_contacto || anuncio.alias_agencia || '').trim();
        const contactoParts = contactoNom.split(/\s+/).filter(Boolean);
        const nombreEsPlaceholder = (v) => !String(v || '').trim() || /^contacto$/i.test(String(v).trim());
        setForm((prev) => {
          const dir = !prev.numero && prev.calle
            ? separarCalleNumero(prev.calle, '')
            : { calle: prev.calle, numero: prev.numero };
          const dirAnuncio = separarCalleNumero(anuncio.direccion || '', '');
          return {
            ...prev,
            url_anuncio: prev.url_anuncio || anuncio.url_original || '',
            nombre: nombreEsPlaceholder(prev.nombre) ? (contactoParts[0] || prev.nombre) : prev.nombre,
            apellido: (!prev.apellido && contactoParts.length > 1)
              ? contactoParts.slice(1).join(' ')
              : prev.apellido,
            telefono: prev.telefono || anuncio.telefonos?.[0] || '',
            email: prev.email || anuncio.emails?.[0] || '',
            calle: prev.calle || dirAnuncio.calle || '',
            numero: prev.numero || dir.numero || dirAnuncio.numero || '',
            poblacion: prev.poblacion || anuncio.municipio || '',
            provincia: prev.provincia || anuncio.provincia || '',
            codigo_postal: prev.codigo_postal || anuncio.codigo_postal || '',
            // Solo tipologías cortas CRM; «Casa o chalet independiente» (portal) no vale.
            tipo_inmueble: prev.tipo_inmueble || tipoCrmUsable(car.tipo_propiedad || anuncio.tipo_inmueble) || '',
            precio: prev.precio || formatearPrecioCampo(anuncio.precio_numerico),
            operacion: prev.operacion || car.operacion || (String(anuncio.url_original || '').includes('/alquiler') ? 'Alquiler' : 'Venta'),
            piso: prev.piso || car.planta || car.piso || '',
            fuente: prev.fuente || normalizarFuenteProcedencia(captacion.fuente, anuncio.portal),
            notas: prev.notas || (anuncio.descripcion ? String(anuncio.descripcion).slice(0, 2000) : ''),
          };
        });
        setFormExtras((prev) => {
          const estadoPortal = car.estado_portal || '';
          const calefPortal = car.calefaccion_tipo || '';
          const cocinaPortal = car.tipo_cocina || car.cocina_tipo_portal || '';
          const piscinaPortal = car.piscina_tipo_portal || car.piscina_tipo || '';
          const orientPortal = car.orientacion_portal || car.orientacion || '';
          return {
            ...prev,
            // No copiar título del anuncio (trae calle) al título CRM — mismo espíritu que tipo portal.
            titulo_portal: prev.titulo_portal || anuncio.titulo || '',
            tipo_portal: prev.tipo_portal
              || car.tipo_propiedad
              || anuncio.tipo_inmueble
              || '',
            resumen: prev.resumen || (anuncio.descripcion ? String(anuncio.descripcion).slice(0, 2000) : ''),
            precio_propietario: prev.precio_propietario
              || formatearPrecioCampo(anuncio.precio_numerico),
            // Portal → *_portal; CRM solo con catálogo (o vacío).
            estado_inmueble_portal: prev.estado_inmueble_portal || estadoPortal || '',
            estado_inmueble_nombre: prev.estado_inmueble_nombre
              || estadoCrmUsable(estadoPortal)
              || '',
            calefaccion_tipo_portal: prev.calefaccion_tipo_portal || calefPortal || '',
            eq_calefaccion: prev.eq_calefaccion || calefaccionCrmUsable(calefPortal) || '',
            cocina_tipo_portal: prev.cocina_tipo_portal || cocinaPortal || '',
            cocina: prev.cocina || cocinaCrmUsable(cocinaPortal) || '',
            piscina_tipo_portal: prev.piscina_tipo_portal || piscinaPortal || '',
            eq_piscina: prev.eq_piscina || piscinaCrmUsable(piscinaPortal) || '',
            orientacion_portal: prev.orientacion_portal || orientPortal || '',
            orientacion_nombre: prev.orientacion_nombre
              || orientacionCrmUsable(orientPortal)
              || '',
          };
        });
        setFotos((prev) => {
          const fotos = Array.isArray(anuncio.fotos) ? anuncio.fotos.filter(Boolean) : [];
          return ampliarFotosSiFaltan(prev, fotos);
        });
      } catch (e) { /* sin anuncio vinculado: noop */ }
    })();
    return () => { cancelled = true; };
  }, [isOpen, captacion?.id]);

  const cargarAgentes = async () => {
    try {
      const res = await api.get('/agentes');
      setAgentes(res.data.data || res.data || []);
    } catch (e) { console.error(e); }
  };

  // Si el id no está en la lista (tipo distinto / agente inactivo) o falta, reconciliar con
  // captacion.agente_id / pipeline_owner / nombre — ampliar, no sustituir la hidratación.
  useEffect(() => {
    if (!isOpen || !agentes.length || !captacion) return;
    setForm((prev) => {
      const cur = prev.agente_id != null && String(prev.agente_id).trim() !== ''
        ? String(prev.agente_id)
        : '';
      if (cur && agentes.some((a) => String(a.id) === cur)) {
        return cur === String(prev.agente_id) ? prev : { ...prev, agente_id: cur };
      }
      const de = datosExtrasCaptacion(captacion);
      const candidatos = [captacion.agente_id, de.pipeline_owner_agente_id]
        .map((v) => (v != null && String(v).trim() !== '' ? String(v) : ''))
        .filter(Boolean);
      for (const id of candidatos) {
        if (agentes.some((a) => String(a.id) === id)) {
          return id === cur ? prev : { ...prev, agente_id: id };
        }
      }
      if (candidatos[0] && candidatos[0] !== cur) {
        return { ...prev, agente_id: candidatos[0] };
      }
      if (!cur) {
        const nom = String(captacion.agente_nombre || '').trim().toLowerCase();
        if (nom) {
          const hit = agentes.find((a) => String(a.nombre || '').trim().toLowerCase() === nom);
          if (hit) return { ...prev, agente_id: String(hit.id) };
        }
      }
      return cur && cur !== String(prev.agente_id || '') ? { ...prev, agente_id: cur } : prev;
    });
  }, [isOpen, agentes, captacion?.id, captacion?.agente_id, captacion?.agente_nombre]);

  const agenteOrphan = useMemo(() => {
    if (!form.agente_id) return null;
    const id = String(form.agente_id);
    if (agentes.some((a) => String(a.id) === id)) return null;
    const de = captacion ? datosExtrasCaptacion(captacion) : {};
    const nombre = captacion?.agente_nombre
      || de.pipeline_owner_nombre
      || 'Agente asignado';
    return { id, nombre };
  }, [form.agente_id, agentes, captacion]);

  const cargarTipos = async () => {
    try {
      const res = await api.get('/auxiliares/tipo-inmueble');
      setTipos(res.data?.data || []);
    } catch (e) { console.error(e); }
    try {
      const resVia = await api.get('/auxiliares/tipo-via');
      setTiposVia(resVia.data?.data || []);
    } catch (e) { console.error(e); }
  };

  const cargarProcedencias = async () => {
    try {
      const res = await api.get('/auxiliares/procedencias');
      const rows = (res.data?.data || []).filter((p) => p?.nombre || p?.id);
      setProcedencias(rows);
    } catch (e) {
      console.error(e);
      setProcedencias([]);
    }
  };

  const procedenciasFijas = useMemo(() => (
    PROCEDENCIAS_FIJAS_NOMBRES
      .map((n) => procedencias.find((p) => slugProcedencia(p.nombre) === slugProcedencia(n)))
      .filter(Boolean)
  ), [procedencias]);

  const portalChip = useMemo(() => {
    const de = captacion ? datosExtrasCaptacion(captacion) : {};
    const candidatos = [
      form.fuente,
      normalizarFuenteProcedencia(captacion?.fuente, de.portal),
      de.portal,
    ].map((x) => String(x || '').trim()).filter(Boolean);
    for (const cand of candidatos) {
      if (!esPortalMarketplaceNombre(cand)) continue;
      const hit = procedencias.find(
        (p) => slugProcedencia(p.nombre) === slugProcedencia(cand) || String(p.id) === String(cand),
      );
      if (hit) return hit;
      return { id: `portal-${slugProcedencia(cand)}`, nombre: normalizarFuenteProcedencia(cand, cand) || cand };
    }
    return null;
  }, [form.fuente, procedencias, captacion]);

  /** Seleccionada desde «+ Procedencias»: va la primera en la fila. */
  const fuenteExtraPrimera = useMemo(() => {
    if (!form.fuente) return null;
    const slug = slugProcedencia(form.fuente);
    const esFija = procedenciasFijas.some(
      (p) => slugProcedencia(p.nombre) === slug || String(p.id) === String(form.fuente),
    );
    const esPortal = portalChip && (
      slugProcedencia(portalChip.nombre) === slug || String(portalChip.id) === String(form.fuente)
    );
    if (esFija || esPortal) return null;
    return procedencias.find(
      (p) => slugProcedencia(p.nombre) === slug || String(p.id) === String(form.fuente),
    ) || { id: `extra-${slug}`, nombre: form.fuente };
  }, [form.fuente, procedenciasFijas, portalChip, procedencias]);

  const procedenciasExtra = useMemo(() => {
    const ocultarSlug = new Set(
      [
        ...procedenciasFijas.map((p) => slugProcedencia(p.nombre)),
        portalChip ? slugProcedencia(portalChip.nombre) : '',
        fuenteExtraPrimera ? slugProcedencia(fuenteExtraPrimera.nombre) : '',
      ].filter(Boolean),
    );
    const ocultarId = new Set(
      [
        ...procedenciasFijas.map((p) => String(p.id)),
        portalChip ? String(portalChip.id) : '',
        fuenteExtraPrimera ? String(fuenteExtraPrimera.id) : '',
      ].filter(Boolean),
    );
    return procedencias.filter(
      (p) => !ocultarId.has(String(p.id)) && !ocultarSlug.has(slugProcedencia(p.nombre)),
    );
  }, [procedencias, procedenciasFijas, portalChip, fuenteExtraPrimera]);

  const chipsProcedenciaFila = useMemo(() => {
    const out = [];
    const seen = new Set();
    const push = (p) => {
      if (!p) return;
      const key = String(p.id || slugProcedencia(p.nombre));
      if (seen.has(key)) return;
      seen.add(key);
      out.push(p);
    };
    push(fuenteExtraPrimera);
    procedenciasFijas.forEach(push);
    push(portalChip);
    return out;
  }, [fuenteExtraPrimera, procedenciasFijas, portalChip]);

  useLayoutEffect(() => {
    if (!procDropdownOpen) {
      setProcMenuPos(null);
      return undefined;
    }
    const place = () => {
      const btn = procBtnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const menuW = 240;
      const left = Math.max(8, Math.min(r.right - menuW, window.innerWidth - menuW - 8));
      setProcMenuPos({ top: r.bottom + 6, left, width: menuW });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [procDropdownOpen]);

  useEffect(() => {
    if (!procDropdownOpen) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (procBtnRef.current?.contains(t)) return;
      if (procDropdownRef.current?.contains(t)) return;
      setProcDropdownOpen(false);
    };
    // click (no mousedown): evita cerrar en el mismo gesto que abre
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [procDropdownOpen]);

  if (!isOpen) return null;

  const cambiar = (campo, valor) => {
    formDirtyRef.current = true;
    setForm(prev => {
      const next = { ...prev, [campo]: valor };
      if (campo === 'telefono' || campo === 'email') {
        relacionContacto.check(
          campo === 'telefono' ? valor : next.telefono,
          campo === 'email' ? valor : next.email,
        );
      }
      return next;
    });
  };
  const toggleChip = (campo, valor) => {
    formDirtyRef.current = true;
    setForm(prev => ({ ...prev, [campo]: prev[campo] === valor ? '' : valor }));
  };

  // ── Subir fotos ──
  const handleFotos = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    formDirtyRef.current = true;

    const nuevasFotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      analizando: false,
      resultado: null,
    }));
    setFotos(prev => [...prev, ...nuevasFotos]);

    // Analizar con Ana solo si hay huecos que pueda rellenar (en Contactados
    // casi siempre ya hay datos → Claude Vision solo alarga y confunde).
    analizarFotos(files);
    e.target.value = '';
  };

  const analizarFotos = async (files) => {
    if (!modDatos) return;
    if (!files.length) return;
    const hayHuecoParaAna = !(
      form.telefono?.trim()
      && form.calle?.trim()
      && form.poblacion?.trim()
      && form.tipo_inmueble?.trim()
    );
    if (!hayHuecoParaAna) return;
    setAnalizando(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('fotos', f));
      const res = await api.post('/captaciones/analizar-fotos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const datos = res.data.datos || {};
      // Ana solo rellena los campos que están vacíos
      setForm(prev => ({
        ...prev,
        telefono:      (!prev.telefono      && datos.telefono)      ? datos.telefono      : prev.telefono,
        calle:         (!prev.calle         && datos.calle)          ? datos.calle         : prev.calle,
        numero:        (!prev.numero        && datos.numero)         ? datos.numero        : prev.numero,
        poblacion:     (!prev.poblacion     && datos.poblacion)      ? datos.poblacion     : prev.poblacion,
        tipo_inmueble: (!prev.tipo_inmueble && datos.tipo_inmueble)  ? datos.tipo_inmueble : prev.tipo_inmueble,
      }));
      if (!form.telefono && datos.telefono) {
        void relacionContacto.checkNow(datos.telefono, form.email);
      }
    } catch (e) {
      console.error('Error analizando fotos:', e);
    } finally {
      setAnalizando(false);
    }
  };

  const eliminarFoto = (idx) => {
    if (!modDatos) return;
    formDirtyRef.current = true;
    setFotos(prev => {
      if (prev[idx]?.file) URL.revokeObjectURL(prev[idx].preview);
      const next = prev.filter((_, i) => i !== idx);
      setLightboxIdx((cur) => {
        if (cur == null) return null;
        if (!next.length) return null;
        if (cur === idx) return Math.min(idx, next.length - 1);
        if (cur > idx) return cur - 1;
        return cur;
      });
      return next;
    });
  };

  const moverFoto = (fromIdx, toIdx) => {
    if (!modDatos) return;
    if (fromIdx === toIdx || fromIdx == null || toIdx == null) return;
    formDirtyRef.current = true;
    setFotos((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  // ── Guardar / Actualizar precaptación ──
  const handleCrear = async () => {
    setError('');
    if (!form.nombre.trim() && !form.telefono.trim() && fotos.length === 0) {
      setError('Añade al menos nombre, teléfono o una foto identificativa');
      return;
    }

    setGuardando(true);
    try {
      const formData = new FormData();
      const { provincia, codigo_postal, bloque, escalera, piso, puerta, tipo_via_id, tipo_via, precio, url_anuncio, idioma, ...formBase } = form;
      Object.entries(formBase).forEach(([k, v]) => { if (v !== '') formData.append(k, v); });
      const precioNum = parsePrecioVentaEntero(precio);
      if (Number.isFinite(precioNum)) formData.append('precio_venta', String(precioNum));
      else if (captacion?.id && String(precio || '').trim() === '') formData.append('precio_venta', '');

      // Incluir datos_extras si algún campo extra está relleno
      const extrasPayload = {};
      if (provincia)     extrasPayload.provincia     = provincia;
      if (codigo_postal) extrasPayload.codigo_postal = codigo_postal;
      // Conservar/sanear poblacion_id: la PWA lo usa; la columna lleva el nombre.
      {
        const prevEx = datosExtrasCaptacion(captacion || {});
        const idPrev = String(prevEx.poblacion_id || '').trim();
        if (idPrev && /^\d{4,5}$/.test(idPrev)) {
          extrasPayload.poblacion_id = idPrev.padStart(5, '0');
        } else if (pareceCodigoInePoblacion(captacion?.poblacion)) {
          extrasPayload.poblacion_id = String(captacion.poblacion).trim().padStart(5, '0');
        }
      }
      if (tipo_via_id)   extrasPayload.tipo_via_id   = tipo_via_id;
      if (tipo_via)      extrasPayload.tipo_via      = tipo_via;
      // Detalle de dirección (mismas claves que ficha propiedad / móvil)
      extrasPayload.bloque = bloque || '';
      extrasPayload.escalera = escalera || '';
      extrasPayload.piso = piso || '';
      extrasPayload.puerta = puerta || '';
      const tituloForm = String(formExtras.titulo || '').trim();
      if (tituloForm && !esTituloDePortalScraping(tituloForm, formExtras)) {
        extrasPayload.titulo = tituloForm;
      } else if (tituloForm && esTituloDePortalScraping(tituloForm, formExtras)) {
        extrasPayload.titulo_portal = tituloForm;
      }
      const tituloPortalForm = String(formExtras.titulo_portal || '').trim();
      if (tituloPortalForm) extrasPayload.titulo_portal = tituloPortalForm;
      const precioExtraRaw = formExtras.precio_propietario || precio || '';
      const precioExtraNum = parsePrecioVentaEntero(precioExtraRaw);
      if (Number.isFinite(precioExtraNum)) {
        extrasPayload.precio_propietario = String(precioExtraNum);
        extrasPayload.precio_venta = Number.isFinite(precioNum)
          ? String(precioNum)
          : String(precioExtraNum);
      }
      if (formExtras.honorarios_porcentaje) extrasPayload.honorarios_porcentaje = formExtras.honorarios_porcentaje;
      if (formExtras.resumen)               extrasPayload.resumen = formExtras.resumen;
      extrasPayload.firma_captacion = formExtras.firma_captacion;
      // Enlace al anuncio (MarketPlace / scraping): solo si hay valor, para no pisar merge
      const urlTrim = String(url_anuncio || '').trim();
      if (urlTrim) extrasPayload.url_original = urlTrim;

      // Idioma del propietario (titular): extras.idioma + propietarios_ficha si existe
      const idiomaNorm = normalizeLanguageCode(idioma) || 'es';
      extrasPayload.idioma = idiomaNorm;
      const prevExtras = datosExtrasCaptacion(captacion || {});
      const patchedIdioma = patchIdiomaEnExtrasCaptacion(prevExtras, idiomaNorm);
      if (Array.isArray(patchedIdioma.propietarios_ficha)) {
        extrasPayload.propietarios_ficha = patchedIdioma.propietarios_ficha;
      }

      if (Object.keys(extrasPayload).length > 0) {
        formData.append('datos_extras', JSON.stringify(extrasPayload));
      }

      // Solo subir fotos nuevas (las existentes ya están en R2)
      fotos.forEach(f => { if (f.file) formData.append('fotos', f.file); });
      const esEdicion = !!captacion;
      // En edición siempre mandar fotos_orden (aunque sea []): si no, al borrar
      // la última existente el backend no recibe la lista y reaparece la foto vieja.
      if (esEdicion) {
        const fotosOrden = fotos
          .filter((f) => f.esExistente && typeof f.preview === 'string')
          .map((f) => f.preview);
        formData.append('fotos_orden', JSON.stringify(fotosOrden));
      }

      const res = esEdicion
        ? await api.patch(`/captaciones/${captacion.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
        : await api.post('/captaciones', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

      if (res.data.ok) {
        formDirtyRef.current = false;
        const avanzado = res.data.autoAvanzado === true;
        setAutoAvanzado(avanzado);
        setCreado(true);
        setTimeout(() => {
          setCreado(false);
          setAutoAvanzado(false);
          resetForm();
          onCreated?.(avanzado);
          onClose?.();
        }, avanzado ? 2000 : 1500);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar la precaptación');
    } finally {
      setGuardando(false);
    }
  };

  const handleArchivar = async () => {
    if (!captacion) return;
    setArchivando(true);
    try {
      const motivo = String(motivoArchivo || '').trim() || null;
      await api.patch(`/captaciones/${captacion.id}/etapa`, {
        etapa: accionArchivo,
        motivo,
        historial_data: {
          tipo: 'cambio_etapa',
          contenido: accionArchivo === 'fria'
            ? (motivo || 'Enviada a Fríos')
            : (motivo || 'Archivada en Eliminados'),
          metadata: {
            etapa_origen: captacion.etapa || 'precaptacion',
            motivo: motivo || null,
          },
        },
      });
      setAccionArchivo(null);
      setMotivoArchivo('');
      onCreated?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al archivar');
    } finally {
      setArchivando(false);
    }
  };

  const resetForm = () => {
    setAccionArchivo(null);
    setMotivoArchivo('');
    setForm({
      nombre: '', apellido: '', telefono: '', email: '',
      fuente: '', operacion: '',
      agente_id: usuario?.agente_id || '',
      quien_trabaja: 'agente',
      notas: '',
      calle: '', numero: '', bloque: '', escalera: '', piso: '', puerta: '',
      poblacion: '', provincia: '', codigo_postal: '', tipo_inmueble: '',
      precio: '',
      tipo_via_id: '', tipo_via: '',
      url_anuncio: '',
      idioma: 'es',
    });
    setFormExtras({ titulo: '', precio_propietario: '', honorarios_porcentaje: '', resumen: '', firma_captacion: false });
    fotos.forEach(f => { if (f.file) URL.revokeObjectURL(f.preview); });
    setFotos([]);
    setLightboxIdx(null);
    setError('');
    setCreado(false);
    setAutoAvanzado(false);
  };

  // ── Estilos ──
  const inputStyle = {
    width: '100%', padding: '8px 12px', fontSize: 13,
    border: `1px solid ${c.border}`, borderRadius: 8,
    background: c.bgInput, color: c.textPrimary, outline: 'none',
    fontFamily: 'inherit', transition: 'border-color 0.15s, background 0.15s, color 0.15s', boxSizing: 'border-box',
  };
  /** Campo con valor → mismo azul que «Todos» del historial + letras blancas. */
  const estiloCampo = (valor, opts = {}) => {
    const faltante = opts.faltanteKey ? esFaltante(opts.faltanteKey) : false;
    const filled = campoTieneValor(valor);
    const base = { ...inputStyle, ...(opts.extra || {}) };
    if (faltante && !filled) {
      return {
        ...base,
        border: '1px solid #ef4444',
        background: isDark ? 'rgba(239,68,68,0.07)' : '#fff5f5',
        color: c.textPrimary,
      };
    }
    if (filled) {
      return {
        ...base,
        background: PRECAP_CAMPO_AZUL,
        border: `1px solid ${PRECAP_CAMPO_AZUL}`,
        color: '#fff',
      };
    }
    return base;
  };
  const cambiarExtra = (campo, valor) => {
    formDirtyRef.current = true;
    setFormExtras(prev => ({ ...prev, [campo]: valor }));
  };
  const labelStyle = {
    display: 'block', marginBottom: 6, fontSize: 10, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.08em', color: c.textMuted,
  };
  // Misma altura visual para Tipo (select), Precio e Operación (chips)
  const controlAlturaPrecap = 36;
  const chipStyle = (activo, color) => {
    const base = {
      padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
      cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
      minHeight: controlAlturaPrecap, boxSizing: 'border-box',
    };
    if (!activo) {
      return {
        ...base,
        border: `1px solid ${c.border}`,
        background: c.bgInput,
        color: c.textSecondary,
      };
    }
    // Operación y Procedencia: azul historial + blanco al activar.
    if (color) {
      return {
        ...base,
        border: `1px solid ${color}`,
        background: `${color}22`,
        color,
      };
    }
    return {
      ...base,
      border: `1px solid ${PRECAP_CAMPO_AZUL}`,
      background: PRECAP_CAMPO_AZUL,
      color: '#ffffff',
    };
  };
  const selectTipoStyle = {
    ...estiloCampo(form.tipo_inmueble, {
      faltanteKey: 'tipo_inmueble_id',
      extra: {
        cursor: modDatos ? 'pointer' : 'not-allowed',
        opacity: modDatos ? 1 : 0.6,
        minHeight: controlAlturaPrecap,
        height: controlAlturaPrecap,
        padding: '0 12px',
      },
    }),
  };

  const modoEdicion = !!captacion?.id;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1050, fontFamily: "'DM Sans', sans-serif",
    }}>
      {/* Contenedor exterior — solo se cierra con ✕ / Cancelar / tras guardar */}
      <div ref={dragRef} style={{
        width: 1400, maxWidth: '97vw', maxHeight: '94vh',
        display: 'flex', flexDirection: 'row',
        background: isDark ? c.bgCard : '#c8c8be', borderRadius: 16, overflow: 'hidden',
        border: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
        boxShadow: isDark
          ? '0 0 60px 15px rgba(255,255,255,0.18), 0 0 120px 40px rgba(255,255,255,0.10)'
          : '0 16px 48px rgba(0,0,0,0.30), 0 4px 12px rgba(0,0,0,0.15)',
        position: pos ? 'fixed' : 'relative',
        ...(pos ? { left: pos.x, top: pos.y, margin: 0 } : {}),
      }}>
      {/* Opciones del select: texto oscuro aunque el select esté relleno (azul/blanco).
          El color de fondo relleno vive en index.css (.precap-campo-lleno) con !important
          para ganar a las reglas globales theme-light/dark de inputs. */}
      <style>{`
        select.precap-campo-lleno option {
          color: #111 !important;
          background: #fff !important;
        }
      `}</style>
      {/* Columna izquierda: formulario */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Confirmación */}
        {creado && (
          <div style={{
            position: 'absolute', inset: 0, borderRadius: 14, background: c.bgCard,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 12, zIndex: 2,
          }}>
            <div style={{ fontSize: 48 }}>{autoAvanzado ? '🚀' : '✅'}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: autoAvanzado ? '#8b5cf6' : ml.green }}>
              {autoAvanzado ? '¡Lista para revisar!' : captacion ? 'Precaptación actualizada' : 'Precaptación creada'}
            </div>
            <div style={{ fontSize: 13, color: c.textMuted, fontWeight: 500, textAlign: 'center', maxWidth: 280 }}>
              {autoAvanzado
                ? 'Todos los campos están completos. La captación avanza a Para Revisar.'
                : form.nombre ? `${form.nombre} ${form.apellido}` : 'Nueva precaptación'
              }
            </div>
          </div>
        )}

        {/* Barra verde superior */}
        <div style={{
          height: 3, flexShrink: 0,
          background: `linear-gradient(90deg, ${ml.greenLight}, ${ml.green}, ${ml.greenFrom}, ${ml.green}, ${ml.greenLight})`,
        }} />

        {/* Header */}
        <div onMouseDown={startDrag} style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexShrink: 0, cursor: 'grab', userSelect: 'none', background: isDark ? 'transparent' : '#e0e0d8', borderBottom: `1px solid ${isDark ? c.border : '#a8a8a0'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, flexShrink: 0,
              background: `linear-gradient(135deg, ${ml.greenFrom}, ${ml.greenTo})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 4px 12px ${ml.greenShadow}`,
            }}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}>
                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: c.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                  {modoEdicion
                    ? ([captacion.nombre, captacion.apellido].filter(Boolean).join(' ') || form.nombre || 'Captación')
                    : (form.nombre ? `${form.nombre}${form.apellido ? ` ${form.apellido}` : ''}` : 'Nueva precaptación')}
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: c.textMuted, fontWeight: 600 }}>
                    Idioma
                    <select
                      value={normalizeLanguageCode(form.idioma) || 'es'}
                      disabled={!modDatos}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        formDirtyRef.current = true;
                        setForm((prev) => ({ ...prev, idioma: e.target.value }));
                      }}
                      title="Idioma del propietario"
                      style={{
                        padding: '4px 8px', borderRadius: 7,
                        border: `1px solid ${c.border}`, background: c.bgInput,
                        color: c.textPrimary, fontSize: 11, fontWeight: 700,
                        fontFamily: 'inherit', cursor: modDatos ? 'pointer' : 'default',
                        outline: 'none',
                      }}
                    >
                      {CAPTACION_IDIOMA_OPTIONS.map((opt) => (
                        <option key={opt.code} value={opt.code}>{opt.code.toUpperCase()}</option>
                      ))}
                    </select>
                  </label>
                  {form.url_anuncio ? (
                    <a
                      href={form.url_anuncio}
                      target="_blank"
                      rel="noopener noreferrer"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        flexShrink: 0, padding: '4px 10px', borderRadius: 7,
                        border: `1px solid ${c.border}`, background: c.bgInput,
                        color: ml.greenLight, fontSize: 11, fontWeight: 600,
                        display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'none',
                        fontFamily: 'inherit', whiteSpace: 'nowrap', cursor: 'pointer',
                      }}
                    >
                      Ver anuncio
                      <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                        <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                        <polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    </a>
                  ) : null}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                {modoEdicion && captacion?.etapa && (() => {
                  const et = ETAPAS_MAPA[captacion.etapa];
                  return et ? (
                    <span style={{ padding: '2px 9px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: `${et.color}20`, color: et.color, border: `1px solid ${et.color}40` }}>
                      {et.label}
                    </span>
                  ) : null;
                })()}
                {!modoEdicion && <p style={{ margin: 0, fontSize: 12, color: c.textMuted }}>Añadir al pipeline de captaciones</p>}
              </div>
            </div>
          </div>
          <button onClick={() => { onClose?.(); resetForm(); }} style={{
            width: 32, height: 32, borderRadius: 8, border: `1px solid ${c.border}`,
            background: c.bgInput, cursor: 'pointer', fontSize: 16, color: c.textMuted,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>✕</button>
        </div>

        {/* Divisor */}
        <div style={{ height: 1, flexShrink: 0, background: `linear-gradient(90deg, transparent, ${c.border}, transparent)`, margin: '6px 24px 0' }} />

        {/* Cuerpo scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>

          {/* ── BANNER CAMPOS FALTANTES ── */}
          {camposFaltantes.length > 0 && (
            <div style={{ margin: '10px 24px 0', padding: '12px 14px', borderRadius: 10,
              background: isDark ? 'rgba(239,68,68,0.10)' : '#fef2f2',
              border: `1px solid ${isDark ? 'rgba(239,68,68,0.35)' : '#fecaca'}`,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#ef4444', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                ⚠️ Campos obligatorios pendientes ({camposFaltantes.length})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {camposFaltantes.map(f => (
                  <span key={f.campo_key} style={{
                    padding: '3px 9px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    background: isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2', color: '#ef4444',
                    border: `1px solid ${isDark ? 'rgba(239,68,68,0.3)' : '#fca5a5'}`,
                  }}>{f.campo_label || f.campo_key}</span>
                ))}
              </div>
            </div>
          )}

          {/* ── FOTOS IDENTIFICATIVAS ── */}
          {verDatos && (
          <div style={{ padding: '8px 24px 0', opacity: modDatos ? 1 : 0.7 }} title={!modDatos ? tituloSinPermiso : undefined}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={labelStyle}>Fotos identificativas</span>
              {analizando && (
                <span style={{ fontSize: 11, color: ml.chipText, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
                  Ana analizando...
                </span>
              )}
            </div>

            {/* Franja horizontal (estilo strip Para Revisar / scraping): 1 fila de miniaturas */}
            <div style={{
              display: 'flex',
              flexWrap: 'nowrap',
              gap: 8,
              marginBottom: 8,
              height: 80,
              maxHeight: 80,
              overflowX: 'auto',
              overflowY: 'hidden',
              alignItems: 'stretch',
              WebkitOverflowScrolling: 'touch',
            }}>
              {fotos.map((foto, idx) => (
                <div key={idx}
                  draggable={modDatos}
                  onDragStart={() => modDatos && setFotoDragIdx(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { if (modDatos) { moverFoto(fotoDragIdx, idx); setFotoDragIdx(null); } }}
                  onDragEnd={() => setFotoDragIdx(null)}
                  title="Arrastra para reordenar"
                  style={{
                    position: 'relative', width: 80, height: 80, borderRadius: 8, overflow: 'hidden',
                    border: `1px solid ${fotoDragIdx === idx ? ml.green : c.border}`,
                    cursor: modDatos ? 'grab' : 'default',
                    flexShrink: 0,
                  }}>
                  <img src={foto.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <div style={{
                    position: 'absolute', bottom: 2, left: 2, padding: '1px 5px', borderRadius: 4,
                    fontSize: 9, fontWeight: 700, background: 'rgba(0,0,0,0.55)', color: '#fff',
                  }}>
                    {idx === 0 ? 'Portada' : `#${idx + 1}`}
                  </div>
                  <button
                    type="button"
                    title="Ver grande"
                    onClick={(e) => { e.stopPropagation(); setLightboxIdx(idx); }}
                    style={{
                      position: 'absolute', top: 2, left: 2,
                      width: 24, height: 24, borderRadius: '50%',
                      background: PRECAP_CAMPO_AZUL, border: '1px solid rgba(255,255,255,0.35)',
                      color: '#fff', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: 0, zIndex: 2,
                    }}
                  >
                    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <circle cx="11" cy="11" r="7" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </button>
                  {modDatos && (
                  <button type="button" onClick={(e) => { e.stopPropagation(); eliminarFoto(idx); }} style={{
                    position: 'absolute', top: 2, right: 2,
                    width: 20, height: 20, borderRadius: '50%',
                    background: 'rgba(0,0,0,0.7)', border: 'none',
                    color: '#fff', fontSize: 12, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>✕</button>
                  )}
                </div>
              ))}
              {modDatos && (
              <button onClick={() => inputFotoRef.current?.click()} style={{
                width: 80, height: 80, borderRadius: 8, flexShrink: 0,
                border: `2px dashed ${c.border}`,
                background: c.bgInput, cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                color: c.textMuted, fontSize: 10, fontWeight: 600,
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = ml.green; e.currentTarget.style.color = ml.green; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = c.border; e.currentTarget.style.color = c.textMuted; }}
              >
                <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                + Foto
              </button>
              )}
            </div>
            <input ref={inputFotoRef} type="file" accept="image/*" multiple onChange={handleFotos} disabled={!modDatos} style={{ display: 'none' }} />
          </div>
          )}

          {/* ── DATOS DEL PROPIETARIO ── */}
          {verDatos && (
          <div style={{ padding: '16px 24px 0' }}>
            <div style={labelStyle}>Datos del propietario</div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1.35fr) minmax(108px, 0.9fr) minmax(0, 1.55fr)',
              gap: 6,
              alignItems: 'stretch',
            }}>
              <input value={form.nombre} onChange={e => cambiar('nombre', e.target.value)} placeholder="Nombre" disabled={!modDatos} title={!modDatos ? tituloSinPermiso : undefined} className={campoTieneValor(form.nombre) ? 'precap-campo-lleno' : undefined} style={{ ...estiloCampo(form.nombre, { faltanteKey: 'propietario', extra: { opacity: modDatos ? 1 : 0.6, cursor: modDatos ? 'text' : 'not-allowed', minWidth: 0 } }) }} />
              <input value={form.apellido} onChange={e => cambiar('apellido', e.target.value)} placeholder="Apellidos" disabled={!modDatos} title={!modDatos ? tituloSinPermiso : undefined} className={campoTieneValor(form.apellido) ? 'precap-campo-lleno' : undefined} style={{ ...estiloCampo(form.apellido, { extra: { opacity: modDatos ? 1 : 0.6, cursor: modDatos ? 'text' : 'not-allowed', minWidth: 0 } }) }} />
              <input value={form.telefono} onChange={e => cambiar('telefono', e.target.value)} placeholder="Teléfono" disabled={!modDatos} title={!modDatos ? tituloSinPermiso : undefined} className={campoTieneValor(form.telefono) ? 'precap-campo-lleno' : undefined} style={{ ...estiloCampo(form.telefono, { extra: { opacity: modDatos ? 1 : 0.6, cursor: modDatos ? 'text' : 'not-allowed', minWidth: 0 } }) }} />
              <input value={form.email} onChange={e => cambiar('email', e.target.value)} placeholder="Email" type="email" disabled={!modDatos} title={!modDatos ? tituloSinPermiso : undefined} className={campoTieneValor(form.email) ? 'precap-campo-lleno' : undefined} style={{ ...estiloCampo(form.email, { extra: { opacity: modDatos ? 1 : 0.6, cursor: modDatos ? 'text' : 'not-allowed', minWidth: 0 } }) }} />
            </div>
          </div>
          )}

          {/* Enlace al anuncio: solo «Ver anuncio» en el header (derecha del nombre). url_anuncio se sigue guardando. */}

          {/* ── DIRECCIÓN DEL INMUEBLE ── */}
          {verDatos && (
          <div style={{ padding: '14px 24px 0' }}>
            {/* Títulos Nº/Blq/Esc/Alt/Pta en la misma línea que «Dirección…» (misma altura de fila) */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 2.4fr) 52px 58px 64px 56px 56px',
              gap: 6,
              marginBottom: 4,
              alignItems: 'end',
            }}>
              <div style={{ ...labelStyle, marginBottom: 0 }}>Dirección del inmueble</div>
              <div style={{ ...labelStyle, marginBottom: 0, textAlign: 'center', padding: 0 }} title="Número">Nº</div>
              <div style={{ ...labelStyle, marginBottom: 0, textAlign: 'center', padding: 0 }} title="Bloque">Blq</div>
              <div style={{ ...labelStyle, marginBottom: 0, textAlign: 'center', padding: 0 }} title="Escalera">Esc</div>
              <div style={{ ...labelStyle, marginBottom: 0, textAlign: 'center', padding: 0 }} title="Altura">Alt</div>
              <div style={{ ...labelStyle, marginBottom: 0, textAlign: 'center', padding: 0 }} title="Puerta">Pta</div>
            </div>
            {/* Fila 1: Calle + Nº + Bloque + Escalera + Altura + Puerta */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 2.4fr) 52px 58px 64px 56px 56px',
              gap: 6,
              marginBottom: 8,
              alignItems: 'stretch',
            }}>
              <div style={{ position: 'relative', minWidth: 0 }}>
                <AddressAutocompleteInput
                  value={form.calle}
                  onValueChange={(v) => cambiar('calle', v)}
                  onPlaceAddressResolved={(p) => {
                    const pob = resolverPoblacionPlaces(p);
                    setForm((prev) => {
                      // No re-pegar el nº en la calle: Places ya lo trae aparte (si no → 2525).
                      const calleRaw = p.calle?.trim() ? p.calle.trim() : prev.calle;
                      const numeroPlaces = p.numero?.trim() || '';
                      const parsed = parseTipoViaCalle(calleRaw, tiposVia);
                      const calle = parsed.nombreVia || calleRaw || prev.calle;
                      let numero = numeroPlaces || parsed.numero || prev.numero || '';
                      // Dedupe si llegó concatenado ("2525" con street_number "25")
                      if (
                        numeroPlaces
                        && parsed.numero
                        && numero === `${numeroPlaces}${parsed.numero}`
                        && numeroPlaces === parsed.numero
                      ) {
                        numero = numeroPlaces;
                      } else if (
                        numero.length >= 2
                        && numero.length % 2 === 0
                        && numero.slice(0, numero.length / 2) === numero.slice(numero.length / 2)
                        && numeroPlaces
                        && numeroPlaces === numero.slice(0, numero.length / 2)
                      ) {
                        numero = numeroPlaces;
                      }
                      let tipo_via_id = parsed.tipoViaId || prev.tipo_via_id || '';
                      let tipo_via = prev.tipo_via || '';
                      if (tipo_via_id) {
                        const tv = tiposVia.find((t) => String(t.id) === String(tipo_via_id));
                        tipo_via = tv?.nombre || parsed.tipoViaCanon || tipo_via;
                      } else if (parsed.tipoViaCanon) {
                        tipo_via = parsed.tipoViaCanon;
                      }
                      return {
                        ...prev,
                        calle: calle || prev.calle,
                        numero: numero || prev.numero,
                        tipo_via_id,
                        tipo_via,
                        poblacion: pob || prev.poblacion,
                        provincia: p.provincia?.trim() ? p.provincia.trim() : prev.provincia,
                        codigo_postal: p.codigo_postal?.trim() ? p.codigo_postal.trim() : prev.codigo_postal,
                      };
                    });
                  }}
                  placeholder="Calle / Urbanización"
                  className={campoTieneValor(form.calle) ? 'precap-campo-lleno' : undefined}
                  style={estiloCampo(form.calle, {
                    faltanteKey: 'nombre_via',
                    extra: { opacity: modDatos ? 1 : 0.6, cursor: modDatos ? 'text' : 'not-allowed' },
                  })}
                  disabled={!modDatos}
                />
              </div>
              <input value={form.numero} onChange={e => cambiar('numero', e.target.value)} placeholder="" disabled={!modDatos} title={!modDatos ? tituloSinPermiso : 'Número'} className={campoTieneValor(form.numero) ? 'precap-campo-lleno' : undefined} style={{ ...estiloCampo(form.numero, { extra: { opacity: modDatos ? 1 : 0.6, cursor: modDatos ? 'text' : 'not-allowed', minWidth: 0 } }) }} />
              <input value={form.bloque} onChange={e => cambiar('bloque', e.target.value)} placeholder="" title="Bloque" disabled={!modDatos} className={campoTieneValor(form.bloque) ? 'precap-campo-lleno' : undefined} style={{ ...estiloCampo(form.bloque, { extra: { opacity: modDatos ? 1 : 0.6, cursor: modDatos ? 'text' : 'not-allowed', minWidth: 0 } }) }} />
              <input value={form.escalera} onChange={e => cambiar('escalera', e.target.value)} placeholder="" title="Escalera" disabled={!modDatos} className={campoTieneValor(form.escalera) ? 'precap-campo-lleno' : undefined} style={{ ...estiloCampo(form.escalera, { extra: { opacity: modDatos ? 1 : 0.6, cursor: modDatos ? 'text' : 'not-allowed', minWidth: 0 } }) }} />
              <input value={form.piso} onChange={e => cambiar('piso', e.target.value)} placeholder="" title="Altura" disabled={!modDatos} className={campoTieneValor(form.piso) ? 'precap-campo-lleno' : undefined} style={{ ...estiloCampo(form.piso, { extra: { opacity: modDatos ? 1 : 0.6, cursor: modDatos ? 'text' : 'not-allowed', minWidth: 0 } }) }} />
              <input value={form.puerta} onChange={e => cambiar('puerta', e.target.value)} placeholder="" title="Puerta" disabled={!modDatos} className={campoTieneValor(form.puerta) ? 'precap-campo-lleno' : undefined} style={{ ...estiloCampo(form.puerta, { extra: { opacity: modDatos ? 1 : 0.6, cursor: modDatos ? 'text' : 'not-allowed', minWidth: 0 } }) }} />
            </div>
            {/* Fila 2: Población · Provincia · C.P. */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: 8 }}>
              <input value={form.poblacion} onChange={e => cambiar('poblacion', e.target.value)} placeholder="Población" disabled={!modDatos} title={!modDatos ? tituloSinPermiso : undefined} className={campoTieneValor(form.poblacion) ? 'precap-campo-lleno' : undefined} style={{ ...estiloCampo(form.poblacion, { faltanteKey: 'poblacion_id', extra: { opacity: modDatos ? 1 : 0.6, cursor: modDatos ? 'text' : 'not-allowed' } }) }} />
              <input value={form.provincia} onChange={e => cambiar('provincia', e.target.value)} placeholder="Provincia" disabled={!modDatos} title={!modDatos ? tituloSinPermiso : undefined} className={campoTieneValor(form.provincia) ? 'precap-campo-lleno' : undefined} style={{ ...estiloCampo(form.provincia, { extra: { opacity: modDatos ? 1 : 0.6, cursor: modDatos ? 'text' : 'not-allowed' } }) }} />
              <input value={form.codigo_postal} onChange={e => cambiar('codigo_postal', e.target.value)} placeholder="C.P." disabled={!modDatos} title={!modDatos ? tituloSinPermiso : undefined} className={campoTieneValor(form.codigo_postal) ? 'precap-campo-lleno' : undefined} style={{ ...estiloCampo(form.codigo_postal, { extra: { opacity: modDatos ? 1 : 0.6, cursor: modDatos ? 'text' : 'not-allowed' } }) }} />
            </div>
          </div>
          )}

          {/* ── TIPO · PRECIO · OPERACIÓN · AGENTE (una línea) ── */}
          {(verDatos || verOperacionFicha) && (
          <div style={{
            padding: '14px 24px 0',
            display: 'grid',
            gridTemplateColumns: (() => {
              if (verDatos && verOperacionFicha) return 'minmax(88px, 0.7fr) minmax(70px, 0.5fr) minmax(200px, 1.2fr) minmax(130px, 0.8fr)';
              if (verDatos) return 'minmax(88px, 0.85fr) minmax(72px, 0.65fr)';
              return 'minmax(200px, 1.2fr) minmax(130px, 0.8fr)';
            })(),
            columnGap: 18,
            rowGap: 10,
            alignItems: 'end',
          }}>
            {verDatos && (
            <div>
              <div style={labelStyle}>Tipo de inmueble</div>
              <select value={form.tipo_inmueble} onChange={e => cambiar('tipo_inmueble', e.target.value)}
                disabled={!modDatos} title={!modDatos ? tituloSinPermiso : undefined}
                className={campoTieneValor(form.tipo_inmueble) ? 'precap-campo-lleno' : undefined}
                style={selectTipoStyle}
              >
                <option value="">— Tipo —</option>
                {tipos.map(t => <option key={t.id} value={t.nombre}>{t.nombre}</option>)}
              </select>
            </div>
            )}
            {verDatos && (
            <div>
              <div style={labelStyle}>Precio</div>
              <input
                value={form.precio}
                onChange={e => cambiar('precio', e.target.value)}
                onBlur={() => cambiar('precio', formatearPrecioCampo(form.precio))}
                placeholder="€"
                inputMode="numeric"
                disabled={!modDatos}
                title={!modDatos ? tituloSinPermiso : undefined}
                className={campoTieneValor(form.precio) ? 'precap-campo-lleno' : undefined}
                style={{
                  ...estiloCampo(form.precio, {
                    extra: {
                      opacity: modDatos ? 1 : 0.6,
                      cursor: modDatos ? 'text' : 'not-allowed',
                      minHeight: controlAlturaPrecap,
                      height: controlAlturaPrecap,
                      padding: '0 12px',
                    },
                  }),
                }}
              />
            </div>
            )}
            {verOperacionFicha && (
            <div>
              <div style={labelStyle}>Operación</div>
              <div style={{ display: 'flex', gap: 6, opacity: modOperacionFicha ? 1 : 0.6, height: controlAlturaPrecap }} title={!modOperacionFicha ? tituloSinPermiso : undefined}>
                {OPERACIONES.map(op => (
                  <button key={op.id} onClick={() => { if (modOperacionFicha) toggleChip('operacion', op.id); }} style={{
                    ...chipStyle(form.operacion === op.id),
                    flex: 1, justifyContent: 'center', display: 'flex', alignItems: 'center', gap: 3, fontSize: 10,
                    height: controlAlturaPrecap, minHeight: controlAlturaPrecap, padding: '0 8px',
                    cursor: modOperacionFicha ? 'pointer' : 'not-allowed',
                  }}>
                    {op.emoji} {op.label}
                  </button>
                ))}
              </div>
            </div>
            )}
            {verOperacionFicha && (
            <div style={{ minWidth: 0 }}>
              <div style={labelStyle}>Agente asignado</div>
              <select
                value={form.agente_id ? String(form.agente_id) : ''}
                onChange={e => cambiar('agente_id', e.target.value)}
                disabled={!modOperacionFicha} title={!modOperacionFicha ? tituloSinPermiso : undefined}
                className={campoTieneValor(form.agente_id) ? 'precap-campo-lleno' : undefined}
                style={{
                  ...estiloCampo(form.agente_id, {
                    extra: {
                      cursor: modOperacionFicha ? 'pointer' : 'not-allowed',
                      opacity: modOperacionFicha ? 1 : 0.6,
                      minHeight: controlAlturaPrecap,
                      height: controlAlturaPrecap,
                      padding: '0 12px',
                    },
                  }),
                }}
              >
                <option value="">— Sin asignar —</option>
                {agenteOrphan && (
                  <option value={agenteOrphan.id}>{agenteOrphan.nombre}</option>
                )}
                {agentes.map(a => (
                  <option key={a.id} value={String(a.id)}>
                    {a.nombre} {String(a.id) === String(usuario?.agente_id || '') ? '(tú)' : ''}
                  </option>
                ))}
              </select>
            </div>
            )}
          </div>
          )}

          {/* ── PROCEDENCIA (una sola línea) ── */}
          {verOperacionFicha && (
          <div style={{ padding: '14px 24px 0' }}>
            <div style={labelStyle}>Procedencia</div>
            {procedencias.length === 0 && (
              <p style={{ fontSize: 11, color: c.textMuted, margin: '0 0 8px', lineHeight: 1.45 }}>
                No hay procedencias en la base de datos. Créalas en Configuración → Tablas auxiliares → Procedencias.
              </p>
            )}
            <div style={{
              display: 'flex', flexWrap: 'nowrap', gap: 6, alignItems: 'center',
              overflowX: 'auto', overflowY: 'visible',
              opacity: modOperacionFicha ? 1 : 0.6,
            }} title={!modOperacionFicha ? tituloSinPermiso : undefined}>
              {chipsProcedenciaFila.map((p) => {
                const nombre = p.nombre || String(p.id);
                const activo = slugProcedencia(form.fuente) === slugProcedencia(nombre)
                  || String(form.fuente) === String(p.id);
                return (
                  <button
                    key={p.id || nombre}
                    type="button"
                    onClick={() => { if (modOperacionFicha) toggleChip('fuente', nombre); }}
                    style={{
                      ...chipStyle(activo),
                      cursor: modOperacionFicha ? 'pointer' : 'not-allowed',
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {emojiVisualProcedencia(nombre, p.emoji)} {nombre}
                  </button>
                );
              })}
              {procedenciasExtra.length > 0 && (
                <button
                  ref={procBtnRef}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!modOperacionFicha) return;
                    setProcDropdownOpen((v) => !v);
                  }}
                  style={{
                    ...chipStyle(false),
                    cursor: modOperacionFicha ? 'pointer' : 'not-allowed',
                    borderStyle: 'dashed',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    // Abierto: solo borde más marcado, nunca estilo «seleccionado» (azul relleno)
                    ...(procDropdownOpen ? { borderColor: PRECAP_CAMPO_AZUL, color: PRECAP_CAMPO_AZUL } : {}),
                  }}
                  title="Ver el resto de procedencias"
                >
                  + Procedencias
                </button>
              )}
            </div>
            {procDropdownOpen && procedenciasExtra.length > 0 && procMenuPos && createPortal(
              <div
                ref={procDropdownRef}
                role="listbox"
                style={{
                  position: 'fixed',
                  top: procMenuPos.top,
                  left: procMenuPos.left,
                  width: procMenuPos.width,
                  zIndex: 13000,
                  maxHeight: 260,
                  overflowY: 'auto',
                  padding: 8,
                  borderRadius: 10,
                  background: isDark ? c.bgCard : '#d8d8d0',
                  border: `1px solid ${c.border}`,
                  boxShadow: '0 12px 28px rgba(0,0,0,0.25)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                {procedenciasExtra.map((p) => {
                  const nombre = p.nombre || String(p.id);
                  const activo = slugProcedencia(form.fuente) === slugProcedencia(nombre)
                    || String(form.fuente) === String(p.id);
                  return (
                    <button
                      key={p.id || nombre}
                      type="button"
                      role="option"
                      aria-selected={activo}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!modOperacionFicha) return;
                        // Queda primera en la fila y seleccionada; + Procedencias no queda activo.
                        cambiar('fuente', nombre);
                        setProcDropdownOpen(false);
                      }}
                      style={{
                        textAlign: 'left',
                        padding: '7px 10px',
                        borderRadius: 7,
                        border: `1px solid ${activo ? PRECAP_CAMPO_AZUL : 'transparent'}`,
                        background: activo ? PRECAP_CAMPO_AZUL : 'transparent',
                        color: activo ? '#ffffff' : c.textPrimary,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {emojiVisualProcedencia(nombre, p.emoji)} {nombre}
                    </button>
                  );
                })}
              </div>,
              document.body,
            )}
          </div>
          )}

          {/* ── NOTAS ── */}
          <div style={{ padding: '14px 24px 0' }}>
            <div style={labelStyle}>Notas</div>
            <textarea
              value={form.notas}
              onChange={e => cambiar('notas', e.target.value)}
              placeholder="Observaciones, contexto, cómo se obtuvo el dato..."
              rows={4}
              className={campoTieneValor(form.notas) ? 'precap-campo-lleno' : undefined}
              style={{ ...estiloCampo(form.notas, { extra: { resize: 'vertical', minHeight: 88, maxHeight: 160, lineHeight: 1.5 } }) }}
            />
          </div>

          {/* Error */}
          {error && (
            <div style={{ padding: '12px 24px 0' }}>
              <div style={{
                padding: '10px 14px', borderRadius: 8, fontSize: 13,
                background: isDark ? '#2a1215' : '#fef2f2',
                border: `1px solid ${isDark ? '#5c1d1d' : '#fecaca'}`,
                color: '#ef4444',
              }}>{error}</div>
            </div>
          )}

          <div style={{ height: 8 }} />
        </div>

        {/* Footer */}
        <div style={{ flexShrink: 0, borderTop: `1px solid ${c.border}` }}>

          {/* Panel motivo archivado */}
          {accionArchivo && (
            <div style={{ padding: '14px 24px', borderBottom: `1px solid ${c.border}`, background: accionArchivo === 'frio' ? (isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff') : (isDark ? 'rgba(239,68,68,0.08)' : '#fef2f2') }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: accionArchivo === 'frio' ? '#3b82f6' : '#ef4444', marginBottom: 8 }}>
                {accionArchivo === 'frio' ? '❄️ Marcar como frío' : '🗑️ Eliminar precaptación'} — Motivo (opcional)
              </div>
              <textarea
                value={motivoArchivo}
                onChange={e => setMotivoArchivo(e.target.value)}
                placeholder="Ej: No contesta, ya tiene agencia, precio irreal..."
                rows={2}
                style={{ ...inputStyle, resize: 'none', marginBottom: 10, lineHeight: 1.5 }}
                onFocus={e => e.target.style.borderColor = accionArchivo === 'frio' ? '#3b82f6' : '#ef4444'}
                onBlur={e => e.target.style.borderColor = c.border}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setAccionArchivo(null); setMotivoArchivo(''); }} style={{
                  flex: 1, padding: '9px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  border: `1px solid ${c.border}`, background: 'transparent', color: c.textMuted,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}>Cancelar</button>
                <button onClick={handleArchivar} disabled={archivando} style={{
                  flex: 2, padding: '9px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                  border: 'none', cursor: archivando ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  background: accionArchivo === 'frio' ? '#3b82f6' : '#ef4444', color: '#fff',
                  opacity: archivando ? 0.6 : 1,
                }}>
                  {archivando ? 'Procesando...' : accionArchivo === 'frio' ? '❄️ Confirmar frío' : '🗑️ Confirmar eliminación'}
                </button>
              </div>
            </div>
          )}

          <div style={{ padding: '14px 20px 18px', display: 'flex', gap: 8, alignItems: 'center', borderTop: `1px solid ${isDark ? c.border : '#a8a8a0'}`, background: isDark ? 'transparent' : '#e0e0d8' }}>
            {/* Botones archivar — solo en modo edición */}
            {captacion && !accionArchivo && (
              <>
                {verFichaFrio && (
                <button onClick={() => { setAccionArchivo('frio'); setMotivoArchivo(''); }} style={{
                  padding: '9px 14px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                  border: `1px solid #3b82f640`, background: isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff',
                  color: '#3b82f6', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                }}>❄️ Frío</button>
                )}
                {verFichaEliminar && (
                <button onClick={() => { setAccionArchivo('eliminado'); setMotivoArchivo(''); }} style={{
                  padding: '9px 14px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                  border: `1px solid #ef444440`, background: isDark ? 'rgba(239,68,68,0.08)' : '#fef2f2',
                  color: '#ef4444', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                }}>🗑️ Eliminar</button>
                )}
              </>
            )}
            <div style={{ flex: 1 }} />
            <button onClick={() => { onClose?.(); resetForm(); }} style={{
              padding: '11px 24px', borderRadius: 10, border: `1.5px solid ${isDark ? c.border : '#a8a8a0'}`,
              background: isDark ? c.bgInput : '#ffffff', color: c.textPrimary,
              cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
            }}>Cancelar</button>
          <button onClick={handleCrear} disabled={guardando} style={{
            padding: '11px 30px', borderRadius: 10, border: 'none',
            background: guardando ? c.bgInput : `linear-gradient(135deg, ${ml.greenFrom}, ${ml.greenTo})`,
            color: guardando ? c.textMuted : ml.greenText,
            cursor: guardando ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
            boxShadow: guardando ? 'none' : `0 4px 14px ${ml.greenShadow}`,
            display: 'flex', alignItems: 'center', gap: 8,
            opacity: guardando ? 0.5 : 1,
          }}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            {guardando ? 'Guardando...' : captacion ? 'Guardar cambios' : 'Crear precaptación'}
          </button>
          </div>
        </div>
      </div>{/* fin columna formulario */}

      {/* Columna derecha: historial (siempre visible) */}
      {modoEdicion ? (
        <PanelHistorialCaptacion
          captacion={captacion ? {
            ...captacion,
            datos_extras: {
              ...datosExtrasCaptacion(captacion),
              idioma: normalizeLanguageCode(form.idioma) || 'es',
            },
          } : captacion}
          isDark={isDark}
          c={c}
          onAvanzado={() => { onCreated?.(); onClose?.(); resetForm(); }}
        />
      ) : (
        <div style={{ flex: 1, minWidth: 0, borderLeft: `1px solid ${c.border}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 32, opacity: 0.45 }}>
          <svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" style={{ color: c.textMuted }}>
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
          <div style={{ fontSize: 13, color: c.textMuted, fontWeight: 600, textAlign: 'center' }}>
            Historial y acciones
          </div>
          <div style={{ fontSize: 11, color: c.textMuted, textAlign: 'center', maxWidth: 200, lineHeight: 1.6 }}>
            El historial aparecerá aquí una vez creada la precaptación
          </div>
        </div>
      )}

      </div>{/* fin contenedor exterior */}

      <ModalRelacionContactoExistente
        open={relacionContacto.open}
        matches={relacionContacto.matches}
        canal={relacionContacto.canal}
        onClose={relacionContacto.dismiss}
        onDescartar={async () => {
          relacionContacto.dismissDiscard();
          if (captacion?.id) {
            try {
              await api.delete(`/captaciones/${captacion.id}`);
            } catch (eDel) {
              console.warn('[relacion-contacto] descartar:', eDel?.message || eDel);
            }
          }
          try { resetForm(); } catch (eR) { /* noop */ }
          onClose?.();
          onCreated?.();
        }}
      />

      {lightboxIdx != null && fotos[lightboxIdx] && (
        <div
          role="presentation"
          onClick={() => setLightboxIdx(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 12000,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            style={{
              position: 'absolute', top: 16, right: 16,
              padding: '8px 14px', borderRadius: 8, border: 'none',
              background: 'rgba(255,255,255,0.15)', color: '#fff',
              fontWeight: 700, fontSize: 13, cursor: 'pointer',
            }}
          >
            Cerrar
          </button>
          {fotos.length > 1 && (
            <>
              <button
                type="button"
                title="Anterior"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIdx((i) => (i > 0 ? i - 1 : fotos.length - 1));
                }}
                style={{
                  position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                  width: 44, height: 44, borderRadius: '50%', border: 'none',
                  background: 'rgba(255,255,255,0.9)', color: '#111',
                  fontSize: 22, fontWeight: 700, cursor: 'pointer',
                }}
              >
                ‹
              </button>
              <button
                type="button"
                title="Siguiente"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIdx((i) => (i < fotos.length - 1 ? i + 1 : 0));
                }}
                style={{
                  position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                  width: 44, height: 44, borderRadius: '50%', border: 'none',
                  background: 'rgba(255,255,255,0.9)', color: '#111',
                  fontSize: 22, fontWeight: 700, cursor: 'pointer',
                }}
              >
                ›
              </button>
            </>
          )}
          <img
            src={fotos[lightboxIdx].preview}
            alt=""
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '96vw', maxHeight: '90vh',
              objectFit: 'contain', borderRadius: 10,
              boxShadow: '0 8px 40px rgba(0,0,0,0.45)',
            }}
          />
          <div style={{
            position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
            color: '#fff', fontSize: 12, fontWeight: 600,
            textShadow: '0 1px 3px rgba(0,0,0,0.8)',
          }}>
            {lightboxIdx + 1} / {fotos.length}
          </div>
        </div>
      )}
    </div>
  );
}